import {computed, Injectable, signal} from '@angular/core';
import {UserResource} from '../resource/user.resource';
import {User, UserAiSettings} from '../interfaces/user.interface';
import {map, Observable, tap} from 'rxjs';
import {
  ACCESS_TOKEN_STORAGE_KEY,
  GUEST_USER_KEY,
  LAST_USER_ID_KEY,
  REFRESH_TOKEN_STORAGE_KEY,
  USER_STORAGE_KEY
} from '../../shared/storage-keys';
import {isEmpty} from 'lodash';
import {isInIframe} from '../../shared/helpers/maporium.validators';

@Injectable({providedIn: 'root'})
export class UserService {
  private readonly _user = signal<User | null>(this.getCurrentUserFromStorage());
  user = computed(() => this._user());

  constructor(private readonly userResource: UserResource) {}

  setUser(user: User) {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    this._user.set(user);
  }

  create(user: User): Observable<void> {
    return this.userResource.create(user);
  }

  createGuestInStorage(user: User): void {
    localStorage.setItem(GUEST_USER_KEY, JSON.stringify(user));
  }

  getGuestUserFromStorage(): User | null {
    const user = JSON.parse(localStorage.getItem(GUEST_USER_KEY) || '{}');
    return !isEmpty(user) ? user : null;
  }

  getOne(id: string): Observable<User> {
    return this.userResource.getOne(id);
  }

  getCurrentLoggedInUser(): Observable<User> {
    return this.userResource.getCurrentLoggedInUser();
  }

  refreshLocalUser(): Observable<User> {
    const user = this.getCurrentUserFromStorage();

    if (!user?.id && !isInIframe()) {
      return this.getCurrentLoggedInUser().pipe(
        tap((res) => {
          this.setUser(res);
          window.location.reload();
        })
      );
    }

    return this.userResource.getOne(user.id).pipe(
      map((res) => {
        this.setUser(res);
        return res;
      })
    );
  }

  update(user: User): Observable<User> {
    return this.userResource.update(user);
  }

  login(login: {email: string, password: string}): Observable<User> {
    return this.userResource.login(login)
      .pipe(map((res) => {
        if (res) {
          localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, res.accessToken);
        }
        return res.user;
      }));
  }

  logout(): void {
    const current = this.getCurrentUserFromStorage();
    if (current?.id) {
      localStorage.setItem(LAST_USER_ID_KEY, current.id);
    }
    localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
  }

  signInWithToken(token: string): Observable<User> {
    return this.userResource.signInWithToken(token)
      .pipe(map((res) => {
        if (res) {
          localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, res.authToken);
          localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, res.refreshToken);
          localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(res.user));
        }
        return res.user;
      }));
  }

  sendSignInLink(email: string): Observable<void> {
    return this.userResource.sendSignInLink(email);
  }
  /**
   * Send a close account confirmation link to the authenticated user.
   */
  sendCloseAccountLink(): Observable<void> {
    return this.userResource.sendCloseAccountLink();
  }
  /**
   * Confirm account closure using a one-time token. Deletes account on success.
   */
  confirmCloseAccount(token: string): Observable<{ wasPaid: boolean }> {
    return this.userResource.confirmCloseAccount(token);
  }

  getCurrentUserFromStorage(): User {
    return JSON.parse(localStorage.getItem(USER_STORAGE_KEY) || '{}');
  }

  getAvailableUsersForMapSharing(mapId: string, q = ''): Observable<User[]> {
    return this.userResource.getAvailableUsersForMapSharing(mapId, q);
  }

  getAvailableUsersForTransfer(q = ''): Observable<User[]> {
    return this.userResource.getAvailableUsersForTransfer(q);
  }

  createPublicUser(dto: {
    name: string;
    lastName: string;
    organization?: string;
    eduDiscount?: boolean;
    bypassTrial?: boolean;
    email: string;
    password?: string;
    planId?: string;
  }): Observable<{
    message: string;
    token?: string;
    user?: User;
  }> {
    return this.userResource.createPublicUser(dto);
  }

  sendWelcomeEmail(userId: string): Observable<void> {
    return this.userResource.sendWelcomeEmail(userId);
  }

  changeEmail(email: string): Observable<void> {
    return this.userResource.changeEmail(email);
  }

  changeBillingName(billingName: string): Observable<void> {
    return this.userResource.changeBillingName(billingName);
  }

  changeServicePlan(packageId: string): Observable<void> {
    return this.userResource.changeServicePlan(packageId);
  }

  changeBillingPeriod(period: 'monthly' | 'yearly'): Observable<void> {
    return this.userResource.changeBillingPeriod(period);
  }

  endTrial(): Observable<void> {
    return this.userResource.endTrial();
  }

  getAiSettings(): Observable<UserAiSettings> {
    return this.userResource.getAiSettings();
  }

  updateAiSettings(updates: Partial<UserAiSettings>): Observable<UserAiSettings> {
    return this.userResource.updateAiSettings(updates);
  }
}
